import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useBewerber } from '../../api/bewerber';
import {
  useCompleteVorstellungsgespraech,
  useCreateOrGetDraft,
  useSaveAnswers,
  useVorstellungsgespraech,
} from '../../api/vorstellungsgespraech';
import { BewerberStatusBadge } from '../../components/bewerber/BewerberStatusBadge';
import { VgAutosaveIndicator } from '../../components/bewerber/vorstellungsgespraech/VgAutosaveIndicator';
import { VgSectionCard } from '../../components/bewerber/vorstellungsgespraech/VgSectionCard';
import { useAuth } from '../../context/AuthContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useDebouncedAutosave } from '../../hooks/useDebouncedAutosave';
import {
  buildDefaultAnswers,
  VORSTELLUNGSGESPRAECH_SECTIONS,
  VorstellungsgespraechStatus,
  type VgAnswers,
  type VgAnswerValue,
  type Vorstellungsgespraech,
} from '../../types/vorstellungsgespraech';

export function VorstellungsgespraechPage() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { user } = useAuth();

  // `?id=` opens one specific interview (a past protocol); without it we open the draft.
  const requestedId = searchParams.get('id');

  const { data, isLoading, error } = useBewerber(id!);
  const existing = useVorstellungsgespraech(id!, requestedId);
  const draftMutation = useCreateOrGetDraft();
  const saveMutation = useSaveAnswers();
  const completeMutation = useCompleteVorstellungsgespraech();

  const [interview, setInterview] = useState<Vorstellungsgespraech | null>(null);
  const [answers, setAnswers] = useState<VgAnswers>({});
  const [draftError, setDraftError] = useState<string | null>(null);

  const bewerber = data?.data;
  const isReadOnly = interview?.status === VorstellungsgespraechStatus.ABGESCHLOSSEN;

  const load = useCallback(
    (loaded: Vorstellungsgespraech, forBewerber: typeof bewerber) => {
      setInterview(loaded);
      // Defaults seed a new draft but must never overwrite a resumed one.
      setAnswers({
        ...(forBewerber ? buildDefaultAnswers(forBewerber) : {}),
        ...(loaded.answers ?? {}),
      });
    },
    [],
  );

  // Open (or resume) the draft once the Bewerber is loaded.
  const hasRequestedDraft = useRef(false);
  const { mutateAsync: openDraft } = draftMutation;
  useEffect(() => {
    if (!bewerber || requestedId || hasRequestedDraft.current) return;
    hasRequestedDraft.current = true;

    openDraft(bewerber.id)
      .then((result) => load(result.data, bewerber))
      .catch((err) => {
        const message =
          err?.response?.data?.message ?? 'Das Vorstellungsgespräch konnte nicht geöffnet werden.';
        setDraftError(Array.isArray(message) ? message.join(', ') : message);
      });
  }, [bewerber, requestedId, openDraft, load]);

  // Load the specific interview when one was requested.
  useEffect(() => {
    if (!requestedId || !existing.data?.data || interview) return;
    load(existing.data.data, bewerber);
  }, [requestedId, existing.data, interview, bewerber, load]);

  useEffect(() => {
    if (requestedId && existing.error) {
      setDraftError('Das Vorstellungsgespräch konnte nicht geladen werden.');
    }
  }, [requestedId, existing.error]);

  const persist = useCallback(
    async (next: VgAnswers) => {
      if (!interview || !bewerber || isReadOnly) return;
      await saveMutation.mutateAsync({
        bewerberId: bewerber.id,
        id: interview.id,
        data: { answers: next },
      });
    },
    [interview, bewerber, isReadOnly, saveMutation],
  );

  const { state: autosaveState, flush } = useDebouncedAutosave(answers, persist, {
    enabled: !!interview && !isReadOnly,
  });

  const handleChange = useCallback((key: string, value: VgAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleBack = async () => {
    await flush();
    navigate(`/bewerber/${id}`);
  };

  const handleComplete = async () => {
    if (!interview || !bewerber) return;

    const confirmed = await confirm({
      title: 'Gespräch abschließen?',
      message:
        'Die Antworten werden in den Bewerber übernommen und als Protokoll gespeichert. Danach können keine Änderungen mehr vorgenommen werden.',
      confirmLabel: 'Abschließen',
      cancelLabel: 'Abbrechen',
    });
    if (!confirmed) return;

    try {
      await flush();
      await completeMutation.mutateAsync({ bewerberId: bewerber.id, id: interview.id });
      toast.success('Vorstellungsgespräch abgeschlossen');
      navigate(`/bewerber/${bewerber.id}`);
    } catch (err: any) {
      const message = err?.response?.data?.message;
      toast.error(
        Array.isArray(message)
          ? message.join(', ')
          : message ?? 'Das Gespräch konnte nicht abgeschlossen werden',
      );
    }
  };

  if (isLoading || (!interview && !draftError)) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-500">Laden...</p>
        </div>
      </div>
    );
  }

  if (error || !bewerber) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-red-600 mb-4">Fehler beim Laden des Bewerbers</p>
          <Link to="/bewerber" className="text-primary-600 hover:text-primary-900">
            Zurück zur Liste
          </Link>
        </div>
      </div>
    );
  }

  if (draftError) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md px-4">
          <p className="text-red-600 mb-4">{draftError}</p>
          <Link to={`/bewerber/${id}`} className="text-primary-600 hover:text-primary-900">
            Zurück zum Bewerber
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      <header className="flex-shrink-0 bg-white border-b border-gray-200 shadow-sm z-20">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center min-w-0">
            <button
              type="button"
              onClick={handleBack}
              className="text-gray-500 hover:text-gray-700 mr-3"
              title="Zurück zum Bewerber"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-lg font-semibold text-gray-900 truncate">
                  Vorstellungsgespräch — {bewerber.firstName} {bewerber.lastName}
                </h1>
                <BewerberStatusBadge status={bewerber.status} />
              </div>
              {isReadOnly ? (
                <span className="text-xs text-gray-500">
                  Abgeschlossen — schreibgeschützt
                </span>
              ) : (
                <VgAutosaveIndicator state={autosaveState} />
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {!isReadOnly && (
              <button
                type="button"
                onClick={handleComplete}
                disabled={completeMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completeMutation.isPending ? 'Wird abgeschlossen...' : 'Gespräch abschließen'}
              </button>
            )}
            <button
              type="button"
              onClick={handleBack}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Zurück zum Bewerber
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6 space-y-3">
          {VORSTELLUNGSGESPRAECH_SECTIONS.map((section, index) => (
            <VgSectionCard
              key={section.key}
              section={section}
              index={index + 1}
              answers={answers}
              onChange={handleChange}
              disabled={isReadOnly}
              interviewerName={user?.firstName}
              bewerberId={id!}
            />
          ))}

          {!isReadOnly && (
            <div className="flex justify-end pt-2 pb-6">
              <button
                type="button"
                onClick={handleComplete}
                disabled={completeMutation.isPending}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {completeMutation.isPending ? 'Wird abgeschlossen...' : 'Gespräch abschließen'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
